// Ralli — SMG's in-house pickleball app.
// All copy, video, and asset URLs live here so you can swap anything without
// touching JSX. When the app goes live, populate `downloads` and flip status.

export const RALLI = {
  status: 'coming-soon', // 'stealth' | 'coming-soon' | 'live'

  brand: {
    name: 'Ralli',
    // Original Ralli palette — court green + deep navy blue.
    primary: '#00E58A',   // court green
    ink: '#0B1F3D',       // deep navy blue
    accent: '#FFD84D',    // umpire yellow accent
  },

  // Official Ralli FORM brand logo — pre-processed transparent PNG hosted
  // locally so no runtime canvas work is needed. Source WebP was decoded
  // once, corner-background stripped with a soft alpha ramp, and saved to
  // /public/assets/ralli-form-logo.png. Swap the file to re-brand.
  logoUrl: '/assets/ralli-form-logo.png',

  // Launch commercial (autoplays muted-loop as the hero video).
  video: {
    url: 'https://customer-assets-jai6qajn.emergentagent.net/job_cinema-hub-693/artifacts/dof31k80_Ralli_Coming_Soon_Launch_Commercial_No_Missed_Hit.mp4',
    posterAlt: 'Ralli — a new pickleball experience coming soon.',
  },

  hero: {
    eyebrow: 'SMG PRODUCTS · 01 / RALLI',
    // The user's tagline
    tagline: 'Changing the Game of Pickleball.',
    subtagline: 'One Play. One Court. One Opportunity — at a time.',
    supporting:
      'Ralli is a new home for pickleball, built from the ground up by SMG. From weekend player to full-time pro — the game gets its next platform.',
    launchLine: 'Coming soon to iOS & Android · Waitlist gets first serve',
  },

  // "What is Ralli" bullets shown in the phone-mockup section.
  // Kept high-level and inviting — enough to hook, not enough to give away
  // the full product roadmap.
  whatIs: {
    eyebrow: '— WHAT IS RALLI',
    heading: 'The whole game, in one app.',
    bullets: [
      { title: 'Video-rich player profiles', desc: 'Show your game. Get seen by players, coaches, and sponsors.' },
      { title: 'Drills, games, and matches', desc: 'From casual courts to competitive brackets — the right level, close to home.' },
      { title: 'Coaches & academies', desc: 'Beginners to pros. Book sessions, find your circle, level up faster.' },
      { title: 'Free for players. Always.', desc: 'No paywall between you and the game.' },
    ],
  },

  // Store links — set to real URLs to activate the badges. Until then the
  // badges render in a "Coming Soon" state.
  downloads: {
    appStore: null,   // 'https://apps.apple.com/us/app/...'
    playStore: null,  // 'https://play.google.com/store/apps/details?id=...'
  },

  waitlist: {
    heading: 'Get on the list.',
    body: 'First on the list, first through the door. Priority access, founder\u2019s perks, and a heads-up before the world sees it.',
    ctaLabel: 'Claim my spot',
    successHeading: 'You\u2019re in.',
    successBody: 'We\u2019ll ping you the moment the curtain drops. Keep an eye on your inbox.',
  },
};

// -------- Competitive positioning (from the Ralli /vs audit) --------
// Feature-level cell values:
//   'full'    → ships this end-to-end (✓)
//   'partial' → limited or workaround support (~)
//   'none'    → not offered (—)
export const RALLI_VS = {
  eyebrow: 'INDEPENDENT MARKET RESEARCH · SEP 2026',
  headline: 'Zero competition by design.',
  supporting:
    'Every platform below does one slice of the game. Ralli is the only system where an athlete plays, gets rated, gets seen, gets coached, gets recruited — and gets paid. One profile. One pipeline.',
  competitors: [
    'Ralli',
    'Pickleheads',
    'DUPR',
    'Pickleball.com',
    'PicklePlay',
    'PickLM',
    'Pickleball Social',
  ],
  capabilities: [
    { name: 'Free athlete pathway',                          cells: ['full','full','none','full','full','partial','partial'] },
    { name: 'Game finding, invites & waitlists',             cells: ['full','full','none','partial','full','full','full'] },
    { name: 'Courts, day passes & reservations',             cells: ['full','full','none','none','none','partial','partial'] },
    { name: 'Tournaments, leagues & live scoring',           cells: ['full','partial','partial','full','partial','full','full'] },
    { name: 'Video-first athlete profiles',                  cells: ['full','none','none','none','none','none','partial'] },
    { name: 'AI match breakdowns & shot charts',             cells: ['full','none','none','none','none','none','none'] },
    { name: 'Coach marketplace + telestration',              cells: ['full','none','none','none','none','partial','none'] },
    { name: 'Recruiter scouting + college hub',              cells: ['full','none','partial','none','none','none','none'] },
    { name: 'Sponsor marketplace & media kits',              cells: ['full','none','none','none','none','none','none'] },
    { name: 'Campaign attribution (links · promo · QR)',     cells: ['full','none','none','none','none','none','none'] },
    { name: 'E-contracts & rights management',               cells: ['full','none','none','none','none','none','none'] },
    { name: 'Ratings: Ralli Rating + DUPR side by side',     cells: ['full','none','full','partial','none','partial','partial'] },
  ],
  headToHead: {
    label: 'HEAD-TO-HEAD',
    title: 'Ralli vs Pickleheads',
    subtitle: 'Live feature audit of pickleheads.com · Sep 2026',
    theirEdge: {
      label: 'Their edge (today)',
      points: [
        '765k+ members · 25,400 court locations · 10,000 cities — the biggest play network',
        '12 round-robin formats, leagues, ladders, organizer payments',
        'Live DUPR sync on round-robin results',
        'Official court finder of USA Pickleball & Global Pickleball Federation',
      ],
    },
    ourEdge: {
      label: 'Where Ralli is untouchable',
      points: [
        'Video profiles with AI breakdowns + shot charts — they have no video at all',
        'Coach marketplace with telestration, playlists, lesson feedback',
        'Recruiter scouting, contact unlocks, college hub with NIL readiness',
        'Sponsor marketplace: media kits, e-contracts, promo/QR attribution, athlete earnings',
        'Court day passes, free-trial passes & reservations — they link out, we transact',
        'Push + SMS ad engine for organizers and brands',
      ],
    },
    punchline:
      'Pickleheads is the best place to find a game. Ralli is the only place a game turns into a rating, a breakdown, a media kit and a paycheck.',
  },
  pillars: [
    {
      label: 'After the score',
      body: 'Competitors record the game. Ralli turns it into a rating, a breakdown, a media kit and an offer.',
    },
    {
      label: 'The commercial layer',
      body: 'No rival lets a brand find an athlete, sign terms, issue a promo code and measure revenue — in one flow.',
    },
    {
      label: 'Video as evidence',
      body: 'HLS highlight reels, AI shot charts and coach telestration. A profile recruiters can actually trust.',
    },
  ],
  footnote:
    'Comparison based on public competitor feature sets, Sep 2026 · Ralli™ · SMG Media Group, LLC',
};

export const RALLI_STRIP = {
  eyebrow: '— SMG PRODUCTS',
  headline: 'Changing the game of pickleball.',
  supporting:
    'Ralli is our in-house product — a new home for pickleball players, coaches, and academies. One play, one court, one opportunity at a time.',
  ctaLabel: 'See Ralli',
  ctaTo: '/ralli',
  status: 'COMING SOON',
};
